import React, { useState, useEffect } from 'react';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';

let toastSubscriber = null;
const recentToasts = new Map();

export function showToast(message, type = 'success') {
  if (!message || typeof message !== 'string') return;
  const cleanMsg = message.trim();
  const now = Date.now();

  // Deduplicate identical error messages for 30 seconds
  const lastTime = recentToasts.get(cleanMsg) || 0;
  const cooldownMs = type === 'error' ? 30000 : 4000;

  if (now - lastTime < cooldownMs) {
    return;
  }

  recentToasts.set(cleanMsg, now);

  if (toastSubscriber) {
    toastSubscriber({ message: cleanMsg, type, id: `${now}-${Math.random()}` });
  }
}

export default function ToastContainer() {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    toastSubscriber = (toast) => {
      setToasts((prev) => {
        // If exact same toast message is already visible on screen, do not add duplicate
        if (prev.some((t) => t.message === toast.message)) {
          return prev;
        }
        return [...prev, toast];
      });

      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== toast.id));
      }, 4500);
    };
    return () => {
      toastSubscriber = null;
    };
  }, []);

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  return (
    <div className="fixed bottom-4 sm:bottom-5 right-4 sm:right-5 left-4 sm:left-auto z-50 flex flex-col space-y-2 sm:max-w-sm w-auto sm:w-full pointer-events-none">
      {toasts.map((toast) => {
        const isSuccess = toast.type === 'success';
        const isError = toast.type === 'error';
        return (
          <div
            key={toast.id}
            className={`pointer-events-auto flex items-start justify-between p-3.5 sm:p-4 rounded-xl shadow-2xl border text-xs sm:text-sm animate-fade-in backdrop-blur-md transition-all ${
              isSuccess
                ? 'bg-[#091812]/95 text-emerald-200 border-emerald-700/80'
                : isError
                ? 'bg-[#1f0b0d]/95 text-rose-200 border-rose-700/80'
                : 'bg-[#091526]/95 text-blue-200 border-blue-700/80'
            }`}
          >
            <div className="flex items-start space-x-2.5 sm:space-x-3 min-w-0 pr-2">
              {isSuccess && <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400 shrink-0 mt-0.5" />}
              {isError && <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-rose-400 shrink-0 mt-0.5" />}
              {!isSuccess && !isError && <Info className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400 shrink-0 mt-0.5" />}
              <span className="font-medium leading-relaxed break-words">{toast.message}</span>
            </div>
            <button
              onClick={() => removeToast(toast.id)}
              className="ml-2 text-slate-400 hover:text-white shrink-0 p-1 rounded-md hover:bg-white/10 transition-colors"
              title="Yopish"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
